import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  // Get the intended destination from location state
  // Default to /app which uses RoleBasedDashboard to route correctly
  const from = location.state?.from?.pathname || '/app';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Navigate to intended destination
        navigate(from, { replace: true });
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = async (userType) => {
    const demoCredentials = {
      td: { email: 'derek@pardsopen.com', password: 'TourneyDir2024!' },
      member: { email: 'member@pardsopen.com', password: 'Member2024!' }
    };

    setFormData(demoCredentials[userType]);
    
    // Auto-submit after a brief delay
    setTimeout(() => {
      handleSubmit({ preventDefault: () => {} });
    }, 100);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Pards Tee Maker</h1>
          <p>Tournament Management System</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="demo-section">
          <p className="demo-title">Demo Accounts</p>
          <div className="demo-buttons">
            <button 
              type="button"
              className="demo-button td-demo"
              onClick={() => handleDemoLogin('td')}
              disabled={loading}
            >
              Tournament Director
            </button>
            <button 
              type="button"
              className="demo-button member-demo"
              onClick={() => handleDemoLogin('member')}
              disabled={loading}
            >
              Member
            </button>
          </div>
        </div>

        <div className="login-info">
          <h3>User Types</h3>
          <div className="user-type-info">
            <div className="user-type">
              <strong>Tournament Director:</strong>
              <ul>
                <li>Manage tournaments and tee sheets</li>
                <li>Member management</li>
                <li>Tournament setup and configuration</li>
                <li>Full system access</li>
              </ul>
            </div>
            <div className="user-type">
              <strong>Member:</strong>
              <ul>
                <li>View personal member record</li>
                <li>Manage wallet and transactions</li>
                <li>View tournament assignments</li>
                <li>Update preferences</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="login-footer">
          <p>Need an account? Contact your Tournament Director</p>
        </div>
      </div>
    </div>
  );
};

export default Login;